import { useCallback, useEffect, useState } from "react";
import { Plus, Send, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { InviteStatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { customer_name: "", email: "", order_number: "", products: "", send_now: true };

const NewInviteDialog = ({ open, onClose, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const save = async () => {
    try {
      await api.post("/admin/invitations", { ...f, products: f.products.split("\n").map((s) => s.trim()).filter(Boolean) });
      toast.success(f.send_now ? "Invito creato, invio in corso" : "Invito programmato");
      setF(EMPTY); onCreated(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-invite-dialog">
        <DialogHeader><DialogTitle>Nuovo invito manuale</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Nome cliente</Label><Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="invite-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Numero ordine</Label><Input value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })} data-testid="invite-order-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Prodotti (uno per riga, facoltativo)</Label><Textarea rows={3} value={f.products} onChange={(e) => setF({ ...f, products: e.target.value })} data-testid="invite-products-input" /></div>
          <label className="sm:col-span-2 flex items-center gap-3 text-sm"><Switch checked={f.send_now} onCheckedChange={(v) => setF({ ...f, send_now: v })} data-testid="invite-send-now-switch" /> Invia subito (altrimenti segue il ritardo impostato)</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button className="bg-emerald-900 hover:bg-emerald-800" onClick={save} data-testid="invite-save-button">Crea invito</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Invitations() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/invitations", { params: { ...(status !== "all" && { status }), ...(q && { q }) } });
    setItems(data.items);
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const sendNow = async (i) => {
    try { await api.post(`/admin/invitations/${i.id}/send`); toast.success("Invito inviato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (i) => { await api.delete(`/admin/invitations/${i.id}`); toast.success("Invito eliminato"); load(); };

  return (
    <div className="fade-up">
      <PageHeader title="Inviti" sub="Creati automaticamente dal webhook WooCommerce o manualmente">
        <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={() => setOpen(true)} data-testid="new-invite-button"><Plus size={16} /> Nuovo invito</Button>
      </PageHeader>
      <div className="flex gap-2 mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid="invite-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem><SelectItem value="scheduled">Programmati</SelectItem><SelectItem value="sent">Inviati</SelectItem>
            <SelectItem value="completed">Recensiti</SelectItem><SelectItem value="failed">Errore</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative"><Search size={15} className="absolute left-3 top-2.5 text-gray-400" /><Input className="pl-9 w-60 bg-white" placeholder="Cerca cliente, email, ordine" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invite-search-input" /></div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <Table data-testid="invitations-table">
          <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Ordine</TableHead><TableHead>Prodotti</TableHead><TableHead>Stato</TableHead><TableHead>Invio previsto</TableHead><TableHead>Inviato</TableHead><TableHead>Promemoria</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`invite-row-${i.id}`}>
                <TableCell><p className="font-medium">{i.customer_name}</p><p className="text-xs text-gray-500">{i.email}</p></TableCell>
                <TableCell>#{i.order_number}<p className="text-xs text-gray-500">{i.source}</p></TableCell>
                <TableCell className="text-sm">{i.products?.length || 0}</TableCell>
                <TableCell><InviteStatusBadge status={i.status} /></TableCell>
                <TableCell className="text-sm">{fmtDateTime(i.send_at)}</TableCell>
                <TableCell className="text-sm">{fmtDateTime(i.sent_at)}</TableCell>
                <TableCell className="text-sm" data-testid={`invite-reminder-${i.id}`}>{i.reminder_sent_at ? fmtDateTime(i.reminder_sent_at) : "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {i.status !== "completed" && <Button size="sm" variant="outline" className="h-8" onClick={() => sendNow(i)} data-testid={`invite-send-${i.id}`}><Send size={14} /> {i.status === "scheduled" ? "Invia ora" : "Reinvia"}</Button>}
                  <Button size="sm" variant="ghost" className="h-8 text-gray-500" onClick={() => del(i)} data-testid={`invite-delete-${i.id}`}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-gray-500 py-12" data-testid="invitations-empty">Nessun invito. Collega il webhook WooCommerce da Integrazioni.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <NewInviteDialog open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}
